<template>
  <PortfolioFormLayout :loading="loading || loadingDependencies">
    <template #header>
      <h5 class="my-2">{{ isUpdateForm ? 'Update' : 'Create' }} Labor Component</h5>
    </template>
    <template #body>
      <LaborComponentFormPresentational
        v-model:cost-pool="costPool"
        v-model:description="description"
        v-model:estimated-labor="estimatedLabor"
        v-model:name="name"
        :cost-pool-options="costPoolOptions"
        :error-messages="errorMessages"
        :loading="loading"
        :server-error="serverError"
        @submit="submit"
      />
    </template>
  </PortfolioFormLayout>
</template>
<script setup lang="ts">
import {Detail} from '@/api/errorMessage';
import {LaborComponentCreateUpdate} from '@/stores/userManagement/userManagement';

const props = defineProps<{
  isUpdateForm: boolean;
  id?: number;
}>();

const router = useRouter();

// Stores
const laborComponentsStore = useLaborComponentsStore();
const costPoolsStore = useCostPoolsStore();
const getLaborComponents = computed(() => laborComponentsStore.getLaborComponents);
const getCostPools = computed(() => costPoolsStore.getCostPools);

const loading = ref(false);
const loadingDependencies = ref(true);

// Form data
const costPool = ref('');
const costPoolOptions = computed(() => getCostPools.value.map((cp) => cp.name));
const description = ref('');
const estimatedLabor = ref(0);
const name = ref('');
const errorMessages = ref(new Map());
const serverError = ref(false);

const submit = async (form: globalThis.Ref<HTMLFormElement | null>) => {
  try {
    loading.value = true;
    const laborComponent: LaborComponentCreateUpdate = {
      cost_pool_id: getCostPools.value.find((cp) => cp.name === costPool.value)!.id,
      description: description.value,
      estimated_labor: estimatedLabor.value,
      name: name.value,
    };
    if (!props.isUpdateForm) {
      await laborComponentsStore.createLaborComponent(laborComponent);
      router.go(-1);
    } else {
      const updateLaborComponent: LaborComponentCreateUpdate = {
        ...laborComponent,
        id: Number(props.id),
      };
      await laborComponentsStore.updateLaborComponent(updateLaborComponent);
      router.go(-1);
    }
  } catch (errorMessage: any) {
    serverError.value = true;
    errorMessages.value = new Map();
    form.value?.classList.remove('was-validated');
    if (typeof errorMessage !== 'string' && errorMessage.length > 0) {
      errorMessage.forEach((d: Detail) => errorMessages.value.set(d.loc[1], d.msg));
    }
    form.value?.classList.remove('was-validated');
    console.log(errorMessage);
  } finally {
    loading.value = false;
  }
};

// TODO: RND-1374
const fetchData = async () => {
  try {
    await costPoolsStore.readCostPools({limit: 500});
  } catch (errorMessage) {
    console.log(errorMessage);
  }
};

const load = async () => {
  await fetchData();
  if (props.isUpdateForm === true && props.id !== undefined) {
    // Try find the item in store state
    let item = getLaborComponents.value.find((lc) => lc.id === props.id);
    // If the item is missing, try to fetch it
    if (item === undefined && props.id !== undefined) {
      try {
        const data = await laborComponentsStore.readLaborComponentById(props.id);
        item = data;
      } catch (error) {
        console.log(error);
      }
    }
    // If the item is found, try populate the form
    if (item !== undefined) {
      let item_ = item;
      const foundCp = getCostPools.value?.find((cp) => cp.id === item_.cost_pool_id);
      if (foundCp) {
        // Set the form data
        costPool.value = foundCp.name;
        description.value = item.description;
        estimatedLabor.value = Number(item.estimated_labor);
        name.value = item.name;
      }
    }
  }
  loadingDependencies.value = false;
};

onMounted(() => load());
</script>
